import React, { useEffect, useState } from "react";
//@ts-ignore
import oldLogo from "../../../assets/images/logo/vld-2026.png";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Typography,
    Box,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faCirclePlus, faXmark } from "@fortawesome/free-solid-svg-icons";
import Socials from "@navbar/socials.navbar";
import { Link as RouterLink } from "react-router-dom";
import { getCampaignStatus } from "@services/campaign.service";
import { useMatomo } from '@jonkoops/matomo-tracker-react';

interface MenuItem {
    label: string;
    path: string;
    accordion: boolean;
    element: React.ReactNode;
}

const items: MenuItem[] = [
    {
        label: "Accueil",
        path: "/",
        accordion: false,
        element: null,
    },
    {
        label: "Actualités",
        path: "/les-actualites",
        accordion: false,
        element: null
    },
    {
        label: "PLUi",
        path: "/carte-interactive",
        accordion: false,
        element: <Typography>PLUi</Typography>,
    },
    {
        label: "Enquêtes",
        path: "/les-enquetes",
        accordion: false,
        element: <Typography>Enquêtes</Typography>,
    },
    {
        label: "Publications",
        path: "/les-publications",
        accordion: false,
        element: <Typography>Publications</Typography>,
    },
    {
        label: "F.A.Q",
        path: "/faq",
        accordion: false,
        element: null,
    },
    {
        label: "Contactez-nous",
        path: "/contactez-nous",
        accordion: false,
        element: null,
    },
];

const Mobile: React.FC = () => {
    const { trackEvent } = useMatomo();
    const [isOpen, setIsOpen] = useState(false);
    const [expanded, setExpanded] = useState<string | false>(false);
    const [campaignActive, setCampaignActive] = useState(false);

    // Tracking de l'ouverture/fermeture du menu mobile
    const toggleMenu = () => {
        const newState = !isOpen;
        setIsOpen(newState);

        trackEvent({
            category: 'Navigation Mobile',
            action: newState ? 'Open' : 'Close',
            name: 'Menu Hamburger',
        });
    };

    // Tracking de l'ouverture des accordéons
    const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);

        if (isExpanded) {
            trackEvent({
                category: 'Navigation Mobile',
                action: 'Expand',
                name: `Accordéon ${panel}`,
            });
        }
    };

    useEffect(() => {
        // Bloque ou débloque le défilement en fonction de l'état du menu
        document.body.style.overflow = isOpen ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [isOpen]);

    useEffect(() => {
        const fetchCampaignStatus = async () => {
            try {
                const status = await getCampaignStatus();
                // @ts-ignore
                setCampaignActive(status.isActive === 1);
            } catch (error) {
                console.error("Erreur lors de la récupération du status de campagne :", error);
            }
        };

        fetchCampaignStatus();
    }, []);

    const dynamicItems: MenuItem[] = [...items];

    // Tracking de la navigation
    const handleSelectNavigation = (itemLabel: string) => {
        trackEvent({
            category: 'Navigation Mobile',
            action: 'Click',
            name: `Menu ${itemLabel}`,
        });

        setIsOpen(false);
        setExpanded(false);
    };

    // Tracking des boutons spécifiques de la campagne
    const handleCampaignButtonClick = (buttonName: string) => {
        trackEvent({
            category: 'Navigation Mobile',
            action: 'Click',
            name: buttonName,
        });

        setIsOpen(false);
        setExpanded(false);
    };

    if (campaignActive) {
        // Modification ici : splice permet d'insérer à l'index 1 (après Accueil qui est 0)
        dynamicItems.splice(1, 0, {
            label: "Municipales 2026",
            path: "/vld-2026",
            accordion: true,
            element: (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Button
                        component={RouterLink}
                        to="/vld-2026/les-publications"
                        variant="contained"
                        sx={{ mb: 2 }}
                        onClick={() => handleCampaignButtonClick('Bouton Tracts 2026 Mobile')}
                    >
                        Tracts 2026
                    </Button>
                    <Button
                        component={RouterLink}
                        to="/vld-2026/programme"
                        variant="contained"
                        sx={{ mb: 2 }}
                        onClick={() => handleCampaignButtonClick('Bouton Programme 2026 Mobile')}
                    >
                        Programme 2026
                    </Button>
                    <Button
                        component={RouterLink}
                        to="/vld-2026/faire-un-don"
                        variant="contained"
                        color="success"
                        sx={{ mb: 2 }}
                        onClick={() => handleCampaignButtonClick('Bouton Faire un Don Mobile')}
                    >
                        Faire un don
                    </Button>
                </Box>
            )
        });
    }

    return (
        <>
            {/* Navbar fixe en haut */}
            <Box
                sx={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: "background.paper",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1,
                    zIndex: 1100,
                    boxShadow: isOpen ? 3 : 1,
                }}
            >
                <Box sx={{ flex: 1 }}>
                    <Box
                        component="img"
                        src={oldLogo}
                        alt=""
                        sx={{ width: 250 }}
                    />
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                    }}
                >
                    <Button
                        onClick={toggleMenu}
                        sx={{ fontSize: "2rem", color: "text.primary" }}
                    >
                        <FontAwesomeIcon icon={isOpen ? faXmark : faBars} />
                    </Button>
                </Box>
            </Box>

            {/* Menu complet en mode mobile */}
            {isOpen && (
                <Box
                    sx={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor: "background.paper",
                        zIndex: 1099,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                    }}
                >
                    {/* Zone supérieure avec la liste des items */}
                    <Box sx={{ pt: "120px", overflowY: "auto" }}>
                        {dynamicItems.map((item, index) =>
                            item.accordion ? (
                                <Accordion
                                    key={index}
                                    expanded={expanded === item.label}
                                    onChange={handleChange(item.label)}
                                    sx={{
                                        boxShadow: "none",
                                        border: "none",
                                        "&:before": { display: "none" },
                                        backgroundColor: "background.paper",
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={
                                            expanded === item.label ? (
                                                <FontAwesomeIcon icon={faXmark} />
                                            ) : (
                                                <FontAwesomeIcon
                                                    icon={faCirclePlus}
                                                    style={{ color: "orange" }}
                                                />
                                            )
                                        }
                                        aria-controls={`${item.label}-content`}
                                        id={`${item.label}-header`}
                                        sx={{ pl: 1 }}
                                    >
                                        <Typography component="span" sx={{ pl: 1 }}>
                                            {item.label}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>{item.element}</AccordionDetails>
                                </Accordion>
                            ) : (
                                <Box key={index} sx={{ p: 2 }}>
                                    <Typography
                                        onClick={() => handleSelectNavigation(item.label)}
                                        component={RouterLink}
                                        to={item.path}
                                        sx={{ textDecoration: "none", color: "text.primary" }}
                                    >
                                        {item.label}
                                    </Typography>
                                </Box>
                            )
                        )}
                    </Box>

                    {/* Socials apparaîtra toujours en bas */}
                    <Box sx={{ p: 2 }}>
                        <Socials />
                    </Box>
                </Box>
            )}

        </>
    );
};

export default Mobile;
